import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { ROUTES } from '@/config/routes';
import { LoadingState } from '@/shared/components/feedback/LoadingState';
import { useAuth } from '../hooks/useAuth';
import type { UserRole } from '../types/auth.types';

export interface ProtectedRouteProps {
  /** When set, the user must hold at least one of these roles. */
  readonly allowedRoles?: readonly UserRole[];
}

/**
 * Route guard for authenticated areas of the app.
 *
 * While a persisted session is being restored it renders a loading state rather than
 * redirecting: without that wait, a signed-in user reloading the page would be bounced to
 * the login screen before the restore completes.
 */
export function ProtectedRoute({ allowedRoles }: ProtectedRouteProps): React.JSX.Element {
  const { isAuthenticated, isRestoringSession, user } = useAuth();
  const location = useLocation();

  if (isRestoringSession) {
    return <LoadingState label="Restoring your session…" testID="session-restore" />;
  }

  if (!isAuthenticated) {
    // Remember where the user was headed so sign-in can return them there.
    return <Navigate to={ROUTES.login} state={{ from: location.pathname }} replace />;
  }

  if (allowedRoles !== undefined && allowedRoles.length > 0) {
    const roles = user?.roles ?? [];
    const permitted = allowedRoles.some((role) => roles.includes(role));
    if (!permitted) {
      return <Navigate to={ROUTES.unauthorized} replace />;
    }
  }

  return <Outlet />;
}
